import React, { useState, useRef } from 'react';
import ReadOnlyField from './../ReadOnly';
import { LoadingIndicator, Typeahead, TypeaheadItem } from '@emorg-prd/standard-react';
import ServicePrincipalService from '../../../../services/ServicePrincipalService';
import FieldHandler from '../../../../models/interfaces/FieldHandler';
import ServicePrincipalLookupObject from '../../../../models/ServicePrincipalLookupObject';
import { useValidation } from '../ValidationContext';
import { TextPassage } from '../../components/TextPassage';
import { TextLink } from '../../components/TextLinkNew';

interface ServicePrincipalLookupProps extends FieldHandler<any> {
	label: string;
	placeholder?: string;
	note?: string;
	noteLink?: string;
	readonly?: boolean;
}

const ServicePrincipalLookup = (props: ServicePrincipalLookupProps) => {
	const servicePrincipalService = new ServicePrincipalService();
	const [searching, setSearching] = useState<boolean>(false);
	const [results, setSearchResults] = useState<ServicePrincipalLookupObject[]>([]);
	const { isValid, nonValidMessage } = useValidation(props.value, props.validation);
	const searchText = useRef<string>('');

	const handleOnChange = (value) => {
		const obj = results.find((o) => o.appId === value);
		searchText.current = obj?.displayName;
		props.onChange(obj);
	};

	const handleSearchUpdate = async (e) => {
		searchText.current = e.target.value;
		const searchTextTrimmed = searchText.current.trim();
		props.onChange(null);
		if (searchTextTrimmed?.length > 2) {
			setSearching(true);
			const response = await servicePrincipalService.servicePrincipalLookup(searchTextTrimmed);
			setSearchResults(response);
			setSearching(false);
		}
	};

	const renderedResults =
		!results?.length || searchText.current?.length < 3 ? (
			<TypeaheadItem value={'0'}>
				<React.Fragment aria-disabled>
					<i>No results found.</i>
				</React.Fragment>
			</TypeaheadItem>
		) : (
			results?.map((sp) => (
				<TypeaheadItem value={sp.appId} key={sp.appId}>
					<React.Fragment>
						<strong>{`${sp.displayName}`}</strong>&nbsp;
						<span>{`(${sp.appId})`}</span>
					</React.Fragment>
				</TypeaheadItem>
			))
		);

	return (
		<>
			{!props.disabled ? (
				<>
					<Typeahead
						optionalClass={`em-u-width-100` + (!isValid ? ' em-has-error' : '')}
						onChange={handleSearchUpdate}
						label={props.label}
						note={nonValidMessage || (!props.noteLink ? props.note : null)}
						aria-disabled={props.disabled}
						clickHandler={(e) => handleOnChange(e)}
						placeholder={props.placeholder}
						value={searchText.current}
					>
						{searching ? <LoadingIndicator size="s" /> : renderedResults}
					</Typeahead>
					{props.noteLink ? (
						<TextPassage>
							<p className="em-c-field__note" style={!isValid ? { color: '#ca6a72' } : null}>
								{props.note}
								<TextLink href={props.noteLink} external>
									More info...
								</TextLink>
							</p>
						</TextPassage>
					) : null}
				</>
			) : (
				<ReadOnlyField label={props.label} text={props.value} />
			)}
		</>
	);
};

export default ServicePrincipalLookup;
